import type { WeightSampleLog } from '../types/production'

import { ensureOfflineReady } from '../offline/initOffline'
import { getActiveTenantKey } from '../offline/tenant'
import { enqueueEntityChange, enqueueEntityChanges } from '../offline/outbox/entityOutbox'
import { getOfflineDb } from '../offline/dbFactory'
import {
  dexieWeightSampleLogRepo,
  type WeightSampleLogListParams
} from '../offline/repos/DexieWeightSampleLogRepo'
import { normalizeToIsoZ } from './dateNormalization'
import {
  buildQueuedEntityMutationResult,
  readEntityBatchSyncStatus,
  retryEntityBatchSyncStatusWithReport,
  type EntityBatchSyncRetryReport,
  type EntityBatchSyncStatus,
  type EntityMutationResult
} from './entityMutationSync'
import { createId } from './id'
import { api } from './api'

const nowIso = (): string => new Date().toISOString()

const toOptionalTrimmed = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

const normalizeWeightGrams = (value: unknown): string => {
  const raw = typeof value === 'number' ? value : Number(String(value ?? '').replaceAll(',', '').trim())
  if (!Number.isFinite(raw)) {
    throw new Error('Weight (g) must be numeric')
  }
  if (raw <= 0) {
    throw new Error('Weight (g) must be greater than 0')
  }
  if (raw > 10000) {
    throw new Error('Weight (g) must be 10,000 g or below')
  }
  return raw.toString()
}

// Mirrors normalizeWeightGrams's shape and the server's lengthMmSchema bound
// (WeightSampleLogService.ts) — PRD `weightsample-individual-length-v1.md` §5.
const normalizeLengthMm = (value: unknown): string | undefined => {
  if (value === undefined || value === null || value === '') return undefined
  const raw = typeof value === 'number' ? value : Number(String(value).replaceAll(',', '').trim())
  if (!Number.isFinite(raw)) {
    throw new Error('Length (mm) must be numeric')
  }
  if (raw <= 0) {
    throw new Error('Length (mm) must be greater than 0')
  }
  if (raw > 2000) {
    throw new Error('Length (mm) must be 2,000 mm or below')
  }
  return raw.toString()
}

const normalizeSampleCount = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined
  const raw = typeof value === 'number' ? value : Number(String(value).trim())
  if (!Number.isFinite(raw)) {
    throw new Error('Sample count must be numeric')
  }
  if (!Number.isInteger(raw)) {
    throw new Error('Sample count must be a whole number')
  }
  if (raw < 1) {
    throw new Error('Sample count must be at least 1')
  }
  return raw
}

const normalizeRecordedAt = (raw?: string): string => {
  if (!raw) return nowIso()
  const normalized = normalizeToIsoZ(raw)
  if (normalized.ok) return normalized.isoZ
  return nowIso()
}

const isOnline = (): boolean => {
  if (typeof navigator === 'undefined') return true
  return navigator.onLine
}

const REMOTE_LIST_TTL_MS = 30_000
const lastRemoteListAtByKey = new Map<string, number>()

const buildRemoteListKey = (params: WeightSampleLogListParams): string => {
  const clean = {
    tenantKey: getActiveTenantKey(),
    farmId: params.farmId?.trim() || '',
    productionUnitId: params.productionUnitId?.trim() || '',
    lot: params.lot?.trim() || '',
    lotId: params.lotId?.trim() || '',
    productionRowId: params.productionRowId?.trim() || '',
    fromRecordedAt: params.fromRecordedAt?.trim() || '',
    toRecordedAt: params.toRecordedAt?.trim() || ''
  }
  return JSON.stringify(clean)
}

const shouldAttemptRemoteList = (params: WeightSampleLogListParams): boolean => {
  if (!isOnline()) return false
  const key = buildRemoteListKey(params)
  const lastAt = lastRemoteListAtByKey.get(key)
  if (!lastAt) return true
  return Date.now() - lastAt > REMOTE_LIST_TTL_MS
}

const getPendingWeightSampleLogIds = async (): Promise<Set<string>> => {
  const entries = await getOfflineDb().entityOutbox.toArray()
  return new Set(
    entries
      .filter((entry) => entry.entityType === 'weightSampleLog')
      .map((entry) => entry.entityId)
  )
}

const hasPendingWeightSampleLogOutboxEntry = async (entityId: string): Promise<boolean> => {
  const trimmed = entityId.trim()
  if (!trimmed) return false

  try {
    const db = getOfflineDb()
    const match = await db.entityOutbox
      .where('[entityType+entityId]')
      .equals(['weightSampleLog', trimmed])
      .first()
    return Boolean(match)
  } catch {
    return false
  }
}

const pruneRemovedRemoteWeightSampleLogs = async (
  cached: WeightSampleLog[],
  remoteMapped: WeightSampleLog[]
): Promise<void> => {
  const remoteIds = new Set(remoteMapped.map((log) => log.id))

  for (const log of cached) {
    if (remoteIds.has(log.id)) continue
    // Preserve local-only changes until their outbox entry is flushed.
    // eslint-disable-next-line no-await-in-loop
    const hasPending = await hasPendingWeightSampleLogOutboxEntry(log.id)
    if (hasPending) continue
    // eslint-disable-next-line no-await-in-loop
    await dexieWeightSampleLogRepo.delete(log.id)
  }
}

const normalizeRemoteWeightSampleLog = (candidate: any): WeightSampleLog | null => {
  if (!candidate || typeof candidate !== 'object') return null
  const id = typeof candidate.id === 'string' ? candidate.id.trim() : ''
  const farmId = typeof candidate.farmId === 'string' ? candidate.farmId.trim() : ''
  const productionUnitId =
    typeof candidate.productionUnitId === 'string' ? candidate.productionUnitId.trim() : ''
  const recordedAtRaw = typeof candidate.recordedAt === 'string' ? candidate.recordedAt.trim() : ''
  const weightGramsRaw = candidate.weightGrams
  if (!id || !farmId || !productionUnitId || !recordedAtRaw) return null

  try {
    return {
      id,
      farmId,
      productionUnitId,
      lot: typeof candidate.lot === 'string' ? candidate.lot.trim() || undefined : undefined,
      lotId: typeof candidate.lotId === 'string' ? candidate.lotId.trim() || undefined : undefined,
      productionRowId:
        typeof candidate.productionRowId === 'string' ? candidate.productionRowId.trim() || undefined : undefined,
      capturedByActorId:
        typeof candidate.capturedByActorId === 'string' ? candidate.capturedByActorId.trim() || undefined : undefined,
      captureSource: toOptionalTrimmed(candidate.captureSource),
      recordedAt: normalizeRecordedAt(recordedAtRaw),
      weightGrams: normalizeWeightGrams(weightGramsRaw),
      lengthMm: normalizeLengthMm(candidate.lengthMm),
      sampleCount: normalizeSampleCount(candidate.sampleCount),
      notes: toOptionalTrimmed(candidate.notes),
      metadata:
        typeof candidate.metadata === 'object' && candidate.metadata !== null ? candidate.metadata : undefined,
      createdAt: typeof candidate.createdAt === 'string' ? candidate.createdAt : undefined,
      updatedAt: typeof candidate.updatedAt === 'string' ? candidate.updatedAt : undefined
    }
  } catch {
    return null
  }
}

export type WeightSampleLogCreatePayload = {
  id?: string
  farmId: string
  productionUnitId: string
  lot?: string
  lotId?: string
  productionRowId?: string
  // Charter D-4: the Crew Mode capture screen stamps the active operator's
  // actorId here. Absent on the admin write path — the server then falls
  // back to the request principal (captureAttribution.ts) so no row is
  // anonymous.
  capturedByActorId?: string
  recordedAt?: string
  weightGrams: string | number
  lengthMm?: string | number
  sampleCount?: number | string
  notes?: string
  metadata?: Record<string, unknown>
}

export type WeightSampleLogUpdatePayload = Partial<{
  lot: string | null
  lotId: string | null
  productionRowId: string | null
  capturedByActorId: string
  recordedAt: string
  weightGrams: string | number
  lengthMm: string | number | null
  sampleCount: number | string | null
  notes: string | null
  metadata: Record<string, unknown> | null
}>

export type WeightSampleLogMutationResult = EntityMutationResult<WeightSampleLog>
export type WeightSampleLogDeleteResult = EntityMutationResult<void>

export type WeightSampleLogWriteOptions = {
  // Server-Side Flag Enforcement v0 (docs/crew-mode/prd/crew-mode-server-side-
  // flag-enforcement-v0.md): set only by Crew Mode's capture screen. Never
  // persisted — carried only in the outbox payload for the server's
  // WeightSampleLogService.create guard to read. The admin app never sets this.
  source?: 'crewMode'
}

const normalizeWeightSampleLogRecord = (args: {
  candidate: WeightSampleLogCreatePayload | WeightSampleLog
  id?: string
  createdAt?: string
  updatedAt?: string
}): WeightSampleLog => {
  const farmId = toOptionalTrimmed(args.candidate.farmId)
  if (!farmId) throw new Error('Facility is required')

  const productionUnitId = toOptionalTrimmed(args.candidate.productionUnitId)
  if (!productionUnitId) throw new Error('Production unit is required')

  const timestamp = nowIso()
  const candidateId =
    typeof (args.candidate as WeightSampleLog).id === 'string'
      ? (args.candidate as WeightSampleLog).id.trim()
      : ''

  return {
    id: args.id?.trim() || candidateId || createId('wsl'),
    farmId,
    productionUnitId,
    lot: toOptionalTrimmed(args.candidate.lot),
    lotId: toOptionalTrimmed(args.candidate.lotId),
    productionRowId: toOptionalTrimmed(args.candidate.productionRowId),
    capturedByActorId: toOptionalTrimmed(args.candidate.capturedByActorId),
    captureSource: toOptionalTrimmed((args.candidate as { captureSource?: unknown }).captureSource),
    recordedAt: normalizeRecordedAt(args.candidate.recordedAt),
    weightGrams: normalizeWeightGrams(args.candidate.weightGrams),
    lengthMm: normalizeLengthMm(args.candidate.lengthMm),
    sampleCount: normalizeSampleCount(args.candidate.sampleCount),
    notes: toOptionalTrimmed(args.candidate.notes),
    metadata: args.candidate.metadata,
    createdAt: args.createdAt?.trim() || timestamp,
    updatedAt: args.updatedAt?.trim() || timestamp
  }
}

export const weightSampleLogApi = {
  readWeightSampleLogSyncStatus: async (logIds: string[]): Promise<EntityBatchSyncStatus> => {
    return readEntityBatchSyncStatus('weightSampleLog', logIds)
  },

  retryWeightSampleLogSyncStatusWithReport: async (
    logIds: string[]
  ): Promise<EntityBatchSyncRetryReport> => {
    return retryEntityBatchSyncStatusWithReport({
      entityType: 'weightSampleLog',
      entityIds: logIds
    })
  },

  list: async (
    params: WeightSampleLogListParams = {},
    options?: { preferLocal?: boolean; forceRemote?: boolean }
  ): Promise<WeightSampleLog[]> => {
    await ensureOfflineReady()

    const [cached, pendingIds] = await Promise.all([
      dexieWeightSampleLogRepo.list(params),
      getPendingWeightSampleLogIds()
    ])
    const shouldFetchRemote = options?.forceRemote ? isOnline() : shouldAttemptRemoteList(params)

    // Same TTL-based pattern as mortalityLogApi (finding #57): `preferLocal`
    // remains in the signature for back-compat but freshness is decided by
    // the `REMOTE_LIST_TTL_MS` gate so peer-device events surface after the
    // TTL window even when the caller opted in to prefer-local.
    if (!shouldFetchRemote) {
      return cached
    }

    const key = buildRemoteListKey(params)
    try {
      const remote = await api.weightSampleLogs.list({
        farmId: params.farmId,
        productionUnitId: params.productionUnitId,
        lot: params.lot,
        lotId: params.lotId,
        productionRowId: params.productionRowId,
        fromRecordedAt: params.fromRecordedAt,
        toRecordedAt: params.toRecordedAt,
        noCache: options?.forceRemote
      })

      const mapped = remote
        .map(normalizeRemoteWeightSampleLog)
        .filter((log): log is WeightSampleLog => Boolean(log))
        .filter((log) => !pendingIds.has(log.id))

      await pruneRemovedRemoteWeightSampleLogs(cached, mapped)
      await dexieWeightSampleLogRepo.upsertMany(mapped)
      lastRemoteListAtByKey.set(key, Date.now())

      return dexieWeightSampleLogRepo.list(params)
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[weightSampleLogApi] remote list failed; falling back to cache', error)
      }
      if (cached.length > 0) return cached
      throw error
    }
  },

  get: async (id: string): Promise<WeightSampleLog | undefined> => {
    await ensureOfflineReady()
    return dexieWeightSampleLogRepo.getById(id)
  },

  create: async (
    payload: WeightSampleLogCreatePayload,
    options?: WeightSampleLogWriteOptions
  ): Promise<WeightSampleLogMutationResult> => {
    await ensureOfflineReady()
    const timestamp = nowIso()
    const log = normalizeWeightSampleLogRecord({
      candidate: payload,
      id: payload.id,
      createdAt: timestamp,
      updatedAt: timestamp
    })

    await dexieWeightSampleLogRepo.upsert(log)
    await enqueueEntityChange({
      entityType: 'weightSampleLog',
      op: 'upsert',
      entityId: log.id,
      payload: {
        ...log,
        id: log.id,
        ...(options?.source ? { source: options.source } : {})
      }
    })

    return buildQueuedEntityMutationResult(log, {
      entityType: 'weightSampleLog',
      entityId: log.id
    })
  },

  saveImportedLogs: async (logs: WeightSampleLog[]): Promise<WeightSampleLog[]> => {
    await ensureOfflineReady()
    if (logs.length === 0) return []

    const timestamp = nowIso()
    const normalizedLogs = logs.map((log) =>
      normalizeWeightSampleLogRecord({
        candidate: log,
        createdAt: typeof log.createdAt === 'string' ? log.createdAt : timestamp,
        updatedAt: timestamp
      })
    )

    await dexieWeightSampleLogRepo.upsertMany(normalizedLogs)
    await enqueueEntityChanges(
      normalizedLogs.map((log) => ({
        entityType: 'weightSampleLog' as const,
        op: 'upsert' as const,
        entityId: log.id,
        payload: {
          ...log,
          id: log.id
        }
      }))
    )

    return normalizedLogs
  },

  update: async (
    id: string,
    patch: WeightSampleLogUpdatePayload
  ): Promise<WeightSampleLogMutationResult> => {
    await ensureOfflineReady()

    const existing = await dexieWeightSampleLogRepo.getById(id)
    if (!existing) throw new Error('Weight sample log not found')

    const next: WeightSampleLog = {
      ...existing,
      lot: Object.prototype.hasOwnProperty.call(patch, 'lot')
        ? toOptionalTrimmed(patch.lot ?? undefined)
        : existing.lot,
      lotId: Object.prototype.hasOwnProperty.call(patch, 'lotId')
        ? toOptionalTrimmed(patch.lotId ?? undefined)
        : existing.lotId,
      productionRowId: Object.prototype.hasOwnProperty.call(patch, 'productionRowId')
        ? toOptionalTrimmed(patch.productionRowId ?? undefined)
        : existing.productionRowId,
      capturedByActorId: Object.prototype.hasOwnProperty.call(patch, 'capturedByActorId')
        ? toOptionalTrimmed(patch.capturedByActorId)
        : existing.capturedByActorId,
      recordedAt: patch.recordedAt ? normalizeRecordedAt(patch.recordedAt) : existing.recordedAt,
      weightGrams: Object.prototype.hasOwnProperty.call(patch, 'weightGrams')
        ? normalizeWeightGrams(patch.weightGrams)
        : existing.weightGrams,
      lengthMm: Object.prototype.hasOwnProperty.call(patch, 'lengthMm')
        ? normalizeLengthMm(patch.lengthMm ?? undefined)
        : existing.lengthMm,
      sampleCount: Object.prototype.hasOwnProperty.call(patch, 'sampleCount')
        ? normalizeSampleCount(patch.sampleCount ?? undefined)
        : existing.sampleCount,
      notes: Object.prototype.hasOwnProperty.call(patch, 'notes')
        ? toOptionalTrimmed(patch.notes ?? undefined)
        : existing.notes,
      metadata: Object.prototype.hasOwnProperty.call(patch, 'metadata')
        ? (patch.metadata ?? undefined)
        : existing.metadata,
      updatedAt: nowIso()
    }

    await dexieWeightSampleLogRepo.upsert(next)
    await enqueueEntityChange({
      entityType: 'weightSampleLog',
      op: 'upsert',
      entityId: next.id,
      payload: {
        ...next,
        id: next.id
      }
    })

    return buildQueuedEntityMutationResult(next, {
      entityType: 'weightSampleLog',
      entityId: next.id
    })
  },

  // Findings #66: every delete carries a reason; the server audits it
  // (CrewDataCorrection with the full prior row) when the outbox syncs.
  delete: async (id: string, audit: { reason: string; operatorActorId?: string }): Promise<WeightSampleLogDeleteResult> => {
    await ensureOfflineReady()

    const existing = await dexieWeightSampleLogRepo.getById(id)

    await dexieWeightSampleLogRepo.delete(id)
    await enqueueEntityChange({
      entityType: 'weightSampleLog',
      op: 'delete',
      entityId: id,
      payload: existing ? { ...existing } : undefined,
      reason: audit.reason,
      operatorActorId: audit.operatorActorId
    })

    return buildQueuedEntityMutationResult(undefined, {
      entityType: 'weightSampleLog',
      entityId: id
    })
  }
}
